import React from 'react';
import { styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ApartmentIcon from '@mui/icons-material/Apartment';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PersonIcon from '@mui/icons-material/Person';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Company } from '../../../interfaces/users.interface';
import { QcIssueStatus } from '../../../interfaces/qcIssue.interface';
import QcStatusTiles from './QcStatusTiles';
import ProgressRing, { ProgressRingProps } from './ProgressRing';
import QcGuidanceBanner from './QcGuidanceBanner';
import { QcGuidance } from './workspaceDerivations';
import { ACCENT_BLUE, ACCENT_BLUE_DARK, SUCCESS_GREEN } from './qcTheme';

const TitleRow = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  margin: '0 0 14px',
});

const PageTitle = styled('h1')({
  fontSize: '1.75rem',
  fontWeight: 300,
  color: '#424242',
  margin: 0,
});

const TitleActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

const BackButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: '#fff',
  border: '1px solid rgba(25,118,210,0.5)',
  color: ACCENT_BLUE,
  borderRadius: '4px',
  padding: '6px 14px',
  fontSize: '0.8125rem',
  fontWeight: 500,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': {
    borderColor: ACCENT_BLUE,
    background: 'rgba(25,118,210,0.04)',
  },
});

const SubmittedBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: 'rgba(46,125,50,0.12)',
  color: SUCCESS_GREEN,
  border: '1px solid rgba(46,125,50,0.4)',
  borderRadius: '4px',
  padding: '6px 14px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
});

const HeaderCard = styled('div')({
  background: '#fff',
  borderRadius: '4px',
  boxShadow: '0 2px 1px -1px rgba(0,0,0,0.2),0 1px 1px 0 rgba(0,0,0,0.14),0 1px 3px 0 rgba(0,0,0,0.12)',
  padding: '10px 24px',
});

const HeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  flexWrap: 'wrap',
});

const CompanyAvatar = styled(Avatar)({
  width: '64px',
  height: '64px',
  border: '1px solid rgba(0,0,0,0.12)',
  background: '#fff',
  color: ACCENT_BLUE_DARK,
  fontWeight: 700,
});

const CompanyBlock = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '3px',
});

const CompanyName = styled('span')({
  fontSize: '1.15rem',
  fontWeight: 700,
  color: '#424242',
  whiteSpace: 'nowrap',
});

const UnitLabel = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: 'rgba(0,0,0,0.42)',
  },
});

const AcceptedLine = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  marginTop: '2px',
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: 'rgba(0,0,0,0.42)',
  },
});

const BlockDivider = styled('div')({
  width: '1px',
  alignSelf: 'stretch',
  background: 'rgba(0,0,0,0.12)',
  margin: '0 4px',
});

const ContactBlock = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  maxWidth: '220px',
});

const ContactCaption = styled('span')({
  fontSize: '0.7rem',
  letterSpacing: '1px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
});

const ContactLine = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.9rem',
  fontWeight: 500,
  color: '#424242',
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: 'rgba(0,0,0,0.42)',
  },
});

const ContactEmail = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.875rem',
  '&, &:link, &:visited, &:hover, &:focus, &:active': {
    color: ACCENT_BLUE,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    color: 'rgba(0,0,0,0.42)',
  },
});

// AcceptedLine's 2px top margin belongs to the SP block only — it levels the assessor row with the contact email row.
const PersonLine = styled(AcceptedLine)({
  marginTop: 0,
});

const RightCluster = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  marginLeft: 'auto',
});


export interface WorkspaceHeaderProps {
  company?: Company;
  unitLabel?: string;
  dateAccepted?: string;
  qcCounts: Record<QcIssueStatus, number>;
  statusFilters: QcIssueStatus[];
  showQcStats: boolean;
  showFirstPassTile: boolean;
  canSubmitQcIssues: boolean;
  canSubmitAssessment: boolean;
  showApproveButton: boolean;
  assessmentApproved: boolean;
  ring: ProgressRingProps;
  guidance: QcGuidance | null;
  isAssessor: boolean;
  assessorCompanyName?: string;
  assessorName?: string;
  showAutofill?: boolean;
  onToggleStatusFilter: (status: QcIssueStatus) => void;
  onSubmitQcIssues: () => void;
  onSubmitAssessment?: () => void;
  onApproveAssessment: () => void;
  onAutofill?: () => void;
}

export default function WorkspaceHeader(props: WorkspaceHeaderProps): JSX.Element {
  const {
    company, unitLabel, dateAccepted, qcCounts, statusFilters, showQcStats, showFirstPassTile,
    canSubmitQcIssues, canSubmitAssessment,
    showApproveButton, assessmentApproved, ring, guidance, isAssessor, assessorCompanyName, assessorName,
    showAutofill,
    onToggleStatusFilter, onSubmitQcIssues, onSubmitAssessment, onApproveAssessment, onAutofill,
  } = props;
  const navigate = useNavigate();
  const contact = company?.primaryContactUser;
  const contactName = contact ? [contact.firstName, contact.lastName].filter(Boolean).join(' ') : undefined;

  return (
    <>
      <TitleRow>
        <PageTitle>Assessment Findings</PageTitle>
        <TitleActions>
          <BackButton type='button' onClick={() => navigate(isAssessor ? '/profile/assessments' : '/admin/assessments')}>
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
            Manage Assessments
          </BackButton>
          {showAutofill && (
            <BackButton type='button' onClick={onAutofill}>
              Autofill Answers
            </BackButton>
          )}
          {canSubmitAssessment && (
            <Button variant='contained' size='small' startIcon={<SendIcon sx={{ fontSize: 17 }} />} onClick={onSubmitAssessment}>
              Submit Assessment
            </Button>
          )}
          {canSubmitQcIssues && (
            <Button variant='contained' size='small' startIcon={<SendIcon sx={{ fontSize: 17 }} />} onClick={onSubmitQcIssues}>
              Submit QC Issues
            </Button>
          )}
          {showApproveButton && (
            <Button variant='contained' size='small' startIcon={<TaskAltIcon sx={{ fontSize: 17 }} />} onClick={onApproveAssessment}>
              All Issues Approved
            </Button>
          )}
          {assessmentApproved && (
            <SubmittedBadge>
              <TaskAltIcon sx={{ fontSize: 16 }} />
              Assessment Approved
            </SubmittedBadge>
          )}
        </TitleActions>
      </TitleRow>
      <QcGuidanceBanner guidance={guidance} />
      <HeaderCard>
        <HeaderRow>
          <CompanyAvatar src={company?.logo || undefined} alt={company?.name || 'Service Provider'}>
            {(company?.name || '?').charAt(0)}
          </CompanyAvatar>
          <CompanyBlock>
            <CompanyName>{company?.name || ''}</CompanyName>
            {unitLabel && (
              <UnitLabel>
                <ApartmentIcon />
                {unitLabel}
              </UnitLabel>
            )}
            {dateAccepted && (
              <AcceptedLine title='Date accepted'>
                <HandshakeIcon />
                {moment(dateAccepted).format('MM/DD/YYYY')}
              </AcceptedLine>
            )}
          </CompanyBlock>
          {(contactName || contact?.email) && (
            <>
              <BlockDivider />
              <ContactBlock>
                <ContactCaption>Primary Contact</ContactCaption>
                {contactName && (
                  <ContactLine>
                    <PersonIcon />
                    {contactName}
                  </ContactLine>
                )}
                {contact?.email && (
                  <ContactEmail href={`mailto:${contact.email}`}>
                    <MailOutlineIcon />
                    {contact.email}
                  </ContactEmail>
                )}
              </ContactBlock>
            </>
          )}
          {(assessorCompanyName || assessorName) && (
            <>
              <BlockDivider />
              {/* Company styled like the Primary Contact name line, assessor name
                  like the SP accepted-date line; block rhythm matches Primary Contact. */}
              <ContactBlock>
                <ContactCaption>Assessor</ContactCaption>
                {assessorCompanyName && (
                  <ContactLine>
                    <BusinessOutlinedIcon />
                    {assessorCompanyName}
                  </ContactLine>
                )}
                {assessorName && (
                  <PersonLine>
                    <PersonIcon />
                    {assessorName}
                  </PersonLine>
                )}
              </ContactBlock>
            </>
          )}
          <RightCluster>
            {showQcStats && (
              <QcStatusTiles
                counts={qcCounts}
                selected={statusFilters}
                showFirstPass={showFirstPassTile}
                onToggle={onToggleStatusFilter}
              />
            )}
            <ProgressRing {...ring} />
          </RightCluster>
        </HeaderRow>
      </HeaderCard>
    </>
  );
}
